import { CloseCode, type HeldLooks, type MovementState } from "odyssey-shared";
import { PAGE_MAP_LIGHTS, forgetSources } from "./world-ui/lighting/map-lights.ts";
import { say } from "../words.ts";
import type { Welcome } from "./connection.ts";
import { closeFailure } from "./loading-failure.ts";
import { gameReport, type PlayerLook } from "./report.ts";
import { PAGE_RULES, rulesMismatch } from "./rules.ts";
import type { SessionCrossing } from "./session-crossing.ts";
import type { SessionHandoff } from "./session-handoff.ts";
import { emptyClientView } from "./handoff-wire.ts";
import type { SessionLifetime, SessionParts } from "./session-types.ts";
import type { WorldSounds } from "./session-sounds.ts";
import { PAGE_CLOCK } from "./server-clock.ts";
import { lightingCrossingWelcome, lightingWelcome } from "./world-ui/lighting/welcome.ts";
import { reloadPageForWorld } from "./world-version.ts";

const RELOAD_GUARD_KEY = "odyssey.reload";
const RELOAD_GUARD_MS = 60_000;

/**
 * Reloads the page to fetch the page Pages serves when the server runs other rules (ADR-069 decision 8), at most once
 * a minute, so a page and a gateway of different builds cannot loop; the guard lives in `sessionStorage` and is
 * skipped when that store is unavailable. A world version the page does not hold reloads by `reloadPageForWorld`
 * instead (ADR-050's amendment of 2026-10-02).
 *
 * @param why What triggered it, for the diagnostics readout.
 * @returns True when the page is reloading; false when the guard held the reload or its store was
 *   unavailable.
 */
function reloadForRules(why: string): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "0");
    if (Date.now() - last < RELOAD_GUARD_MS) {
      gameReport.closed = `version changed (${why}); reload held, last reload ${Math.round((Date.now() - last) / 1000)}s ago`;
      return false;
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * The look the view's players hold, for the report: each handle a look has arrived for, in handle order.
 *
 * @param looks The session's held looks.
 * @returns One entry per player holding a look.
 */
function playerLookReadings(looks: HeldLooks): PlayerLook[] {
  return [...looks.held].map(([handle, look]) => ({ handle, ...look })).sort((a, b) => a.handle - b.handle);
}

/**
 * Seeds the predictor from the spawn and creates the local character. A welcome starts a session, whose handles
 * and states owe nothing to any before it, so the transition playback drops what it held and every thing's next
 * record is first sight (ADR-058 amendment 3); a welcome to another world version reloads the page into the version
 * it names (`reloadPageForWorld`, ADR-050's amendment of 2026-10-02), and one naming rules other than the page's
 * (ADR-069 decision 8) reloads it under the rules guard, either dropping it too; when the reload is held
 * the page gives the connection up and shows the failure, which says the world, or for other rules the game, has
 * been updated, with Reload, under the refused title without a code, since the server sent none. The welcome
 * does not reveal the world: the loading shell moves to its last step, the
 * chunks around the camera, and a drawn frame reveals it (`reveal`). It names the player: the vitals frame shows
 * the full name `welcome` carries, the map server's own name for the entity, which the nameplate over it draws too
 * (ADR-067 decision 4).
 *
 * @param parts The session's parts.
 * @param lifetime The session's own moves.
 * @param handoff The session's moves between maps, which stand the page on the map the welcome names.
 * @param sounds The listeners that sound the world's changes.
 * @param welcome The welcome message.
 * @param crossing The page's side of seamless travel, which takes the welcome of a seamless crossing (ADR-147 decision
 *   18) and stands the other maps' figures on the map any other welcome names.
 */
export function receiveWelcome(parts: SessionParts, lifetime: SessionLifetime, handoff: SessionHandoff, sounds: WorldSounds, welcome: Welcome, crossing: SessionCrossing): void {
  const { options, host, state, local, remotes } = parts;
  const world = host.world();
  if (world === null) {
    return;
  }
  const worldMoved = welcome.worldVersion !== world.version;
  const detail = worldMoved ? `world ${welcome.worldVersion} differs from loaded ${world.version}` : rulesMismatch(welcome.rulesVersion, PAGE_RULES.version);
  if (detail !== null) {
    gameReport.closed = detail;
    if (!(worldMoved ? reloadPageForWorld(welcome.worldVersion, "welcome") : reloadForRules("welcome"))) {
      lifetime.giveUp({ ...closeFailure(worldMoved ? CloseCode.WorldUpdated : CloseCode.VersionMismatch, "", options.account), title: say("loading.failure.refused"), detail });
    }
    return;
  }
  forgetSources(PAGE_MAP_LIGHTS);
  if (handoff.crossing) {
    if (welcome.crossing !== undefined && crossing.welcome(welcome, { forgetMap: () => lifetime.crossed(), landed: () => handoff.crossed() })) {
      const now = host.world();
      if (now !== null) {
        now.things.onTransition = options.sounds === undefined ? null : sounds.thingChanged;
        now.projectiles.onLanded = options.sounds === undefined ? null : sounds.projectileLanded;
      }
      gameReport.welcomed = true;
      gameReport.handle = welcome.handle;
      lightingCrossingWelcome(gameReport.lighting);
      return;
    }
    const view = parts.connection()?.view;
    if (view !== undefined) {
      emptyClientView(view);
    }
    handoff.fallBack();
  }
  if (!handoff.standing(welcome, (again) => receiveWelcome(parts, lifetime, handoff, sounds, again, crossing))) {
    return;
  }
  lifetime.reset();
  world.things.resetPlayback();
  world.things.onTransition = options.sounds === undefined ? null : sounds.thingChanged;
  world.projectiles.onLanded = options.sounds === undefined ? null : sounds.projectileLanded;
  world.presence.reset();
  state.handle = welcome.handle;
  const start: MovementState = { x: welcome.spawn.x, y: welcome.spawn.y, facing: 0 };
  local.begin(start, world.grid, (host.ownCharacter ?? host.character)(), PAGE_RULES.constants.walk_speed, PAGE_RULES.constants.run_speed);
  remotes.begin(world.grid, welcome.tick);
  crossing.stand(host.world() ?? world, welcome);
  state.welcomed = true;
  if (state.handoff !== null) {
    state.handoff.landed = true;
  }
  gameReport.welcomed = true;
  lightingWelcome(gameReport.lighting, PAGE_CLOCK, welcome.serverTime, performance.now());
  gameReport.playerLooks = () => playerLookReadings(parts.looks);
  gameReport.handle = welcome.handle;
  gameReport.name = { firstName: welcome.firstName, familyName: welcome.familyName };
  options.hud.setName(welcome.firstName, welcome.familyName);
  options.progress({ type: "finished", step: "welcome" });
  options.progress({ type: "started", step: "view", total: 1 });
}
